import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from '../../../icons';
import './FoldRow.css';

/**
 * FoldRow — 한 줄 접힘 (2026-10-18 · web 레인 W10-61 · 렌즈 Toss · 계획 8.2 «61 직위»)
 *
 * 늘 펼쳐 두던 곁 패널을 «한 줄 요약 + 목록 보기»로 접는다. 펼치면 **그 자리에서** 내용이 그대로 나온다
 * (다른 화면으로 가지 않는다). 61 직위가 처음 쓰고, 62 직책도 같은 꼴로 맞출 수 있다(이 판 밖).
 *   ★ 접힌 내용은 그리지 않는다 — 낭독기 · Tab 이 보이지 않는 칸으로 들어가지 않는다.
 *   ★ 펼침 상태는 `aria-expanded` 로 알리고, 펼쳤을 때만 `aria-controls` 가 그 몸을 가리킨다.
 */
export interface FoldRowProps {
  /** 접힌 줄에 늘 보이는 한 줄 — 숫자를 남긴다(«N개 중 K개» 등). */
  summary: ReactNode;
  children: ReactNode;
  openLabel?: string;
  closeLabel?: string;
  defaultOpen?: boolean;
  className?: string;
}

export function FoldRow({
  summary, children, openLabel = '목록 보기', closeLabel = '접기', defaultOpen = false, className,
}: FoldRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className={['wc-fold', open ? 'is-open' : '', className ?? ''].filter(Boolean).join(' ')}>
      <div className="wc-fold__row">
        <span className="wc-fold__sum">{summary}</span>
        <button
          type="button" className="wc-fold__btn"
          aria-expanded={open} aria-controls={open ? id : undefined}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? closeLabel : openLabel}
          {open ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
        </button>
      </div>
      {open && <div id={id} className="wc-fold__body">{children}</div>}
    </div>
  );
}
